/**
 * Client API centralisé
 *
 * Gère automatiquement :
 * - Injection du token JWT
 * - Redirection vers /login en cas de 401
 * - Normalisation des réponses
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api';

/** Réponse normalisée de l'API */
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  details?: Record<string, string>;
}

/** Récupère le token JWT depuis le localStorage */
function getToken(): string | null {
  return localStorage.getItem('token');
}

/** Stocke le token JWT */
export function setToken(token: string): void {
  localStorage.setItem('token', token);
}

/** Supprime le token JWT */
export function removeToken(): void {
  localStorage.removeItem('token');
}

/**
 * Effectue une requête vers l'API avec gestion automatique du JWT
 */
export async function apiFetch<T = unknown>(
  endpoint: string,
  options?: RequestInit & { timeout?: number },
): Promise<ApiResponse<T>> {
  const token = getToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  // Fusionner les headers sans écraser les headers custom
  if (options?.headers) {
    const customHeaders = options.headers as Record<string, string>;
    Object.assign(headers, customHeaders);
  }

  const controller = new AbortController();
  const timeoutMs = options?.timeout ?? 15000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof DOMException && err.name === 'AbortError') {
      return { success: false, error: 'Délai de connexion dépassé' };
    }
    throw err;
  }
  clearTimeout(timeoutId);

  // Token expiré ou invalide → rediriger vers le login
  if (response.status === 401) {
    removeToken();
    localStorage.removeItem('user');
    window.location.href = '/login';
    return { success: false, error: 'Session expirée' };
  }

  const data = (await response.json()) as ApiResponse<T>;
  return data;
}

/**
 * Upload de fichier (multipart/form-data) — pas de Content-Type auto
 */
export async function apiUpload<T = unknown>(
  endpoint: string,
  formData: FormData,
): Promise<ApiResponse<T>> {
  const token = getToken();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof DOMException && err.name === 'AbortError') {
      return { success: false, error: 'Délai de connexion dépassé' };
    }
    throw err;
  }
  clearTimeout(timeoutId);

  if (response.status === 401) {
    removeToken();
    localStorage.removeItem('user');
    window.location.href = '/login';
    return { success: false, error: 'Session expirée' };
  }

  const data = (await response.json()) as ApiResponse<T>;
  return data;
}
